import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Box, Button, TextArea, Text, ShadowBox } from '@components/common';
import { TableHeading } from '@components/Typography';
import TableCell from '@components/TableCell';
import DialogBox from '@components/DialogBox';
import CancelConfirm from '@containers/booking/Confirm/CancelConfirm';
import TimeWindowModal from '@containers/booking/TimeWindowModal';

import { BookingSelectors } from '@redux/BookingRedux';
import CleansActions, { CleansSelectors } from '@redux/CleansRedux';
import { AppSelectors } from '@redux/AppRedux';
import { AuthSelectors } from '@redux/AuthRedux';

import { trackCleaningCanceled } from '@services/analytics';
import {
  trackCleaningUpdated,
  trackOnetimeAdditionalInstructions
} from '@services/analytics';
import {
  BOOKING_STAGE,
  SERVICE_TYPES,
  FREQUENCIES,
  TIME_FRAME
} from '@services/minte';

import history from '@utils/history';
import API from '@utils/api';
import { getErrors } from '@utils/error';
import { isWithin24Hours } from '@utils/date';
import { transfromPropertyToAddress } from '@services/transform';

// TODO: need to check time frame availability for the certain date
const ALL_TIME_FRAMES = [
  TIME_FRAME.ANYTIME,
  TIME_FRAME.MORNING,
  TIME_FRAME.AFTERNOON
];

function CleanEdit(props) {
  const {
    currentClean,
    frequencyData,
    setCleanEditing,
    isPMUser,
    recipient,
    updateCurrentClean
  } = props;
  const {
    bookingID: bookingId,
    isRecurring,
    serviceType: serviceTypeObj,
    notes,
    transactions,
    status,
    momentServiceDate,
    serviceDateYmdString,
    property
  } = currentClean;

  const fullAddress = transfromPropertyToAddress(property);

  const frequency = isRecurring ? FREQUENCIES.REGULARLY : FREQUENCIES.ONCE;
  const { fullTitle } = frequencyData[frequency];
  const { name: serviceType } = serviceTypeObj;
  const isHotelStyle = serviceType === SERVICE_TYPES.HOTEL_STYLE;

  const isPaid = transactions && transactions.status === 'success';
  const isServiceWithin24hours = isWithin24Hours(momentServiceDate);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [show, showModal] = useState(false);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [originalNote, setOriginalNote] = useState(notes || '');
  const [hintNote, setHintNote] = useState(notes || '');
  const [isNoteEditing, setIsNoteEditing] = useState(false);

  const handleEditServceType = async () => {
    setCleanEditing(BOOKING_STAGE.SERVICE_TYPE);
    history.push(`${history.location.pathname}/edit`);
  };

  const handleEditDate = async () => {
    setCleanEditing(BOOKING_STAGE.SERVICE_DATE);
    history.push(`${history.location.pathname}/edit`);
  };

  const handleCancelBooking = async () => {
    setIsSubmitting(true);
    setErrMsg('');
    const { currentClean } = props;
    try {
      const cancelResp = await API.cancelClean(bookingId);
      trackCleaningCanceled(currentClean, cancelResp);
      history.goBack();
    } catch (err) {
      const error = getErrors(err);
      if (error.message) {
        setErrMsg(error.message);
      } else {
        setErrMsg(Object.values(error).pop || err.message);
      }
    }

    setIsSubmitting(false);
  };

  const handleEditTimeWindow = async newTimeWindow => {
    const { timeframe, additionalService, propertyID } = currentClean;

    if (timeframe === newTimeWindow) {
      return;
    }
    setIsSubmitting(true);

    try {
      const { result } = await API.updateBooking(bookingId, {
        paymentMethod: 'credit',
        additionalService: (additionalService || []).map(item => item.name),
        serviceType,
        timeframe: newTimeWindow,
        serviceDate: serviceDateYmdString,
        isPMUser,
        recipient,
        propertyID
      });
      trackCleaningUpdated(result);
      updateCurrentClean(bookingId, result);
    } catch (err) {
      const error = getErrors(err);
      if (error.message) {
        setErrMsg(error.message);
      } else {
        setErrMsg(Object.values(error).pop || err.message);
      }
    }
    setIsSubmitting(false);
  };

  const handleUpgradeClean = async () => {
    setCleanEditing(BOOKING_STAGE.CONFIRM, {
      serviceType: SERVICE_TYPES.DEEP_CLEAN
    });
    history.push(`${history.location.pathname}/edit`);
  };

  const handleChangeNote = e => {
    const { value } = e.target;
    setHintNote(value);

    if (value !== originalNote && value !== '') {
      setIsNoteEditing(true);
    } else {
      setIsNoteEditing(false);
    }
  };

  const handleSubmitNote = async () => {
    setIsSubmitting(true);
    await API.updateBookingNote(bookingId, hintNote);
    setOriginalNote(hintNote);
    setIsSubmitting(false);
    setIsNoteEditing(false);
    trackOnetimeAdditionalInstructions(bookingId, hintNote);
  };

  const renderCleanInfo = () => {
    const {
      formattedDate,
      additionalService,
      serviceType,
      timeframe,
      timeframeTitle
    } = currentClean;
    const { title, icon } = serviceType;
    const addonIcons =
      additionalService && additionalService.length > 0
        ? additionalService.map(({ icon }) => icon)
        : undefined;
    const addonIconTooltips =
      additionalService && additionalService.length > 0
        ? additionalService.reduce(
            (acc, { icon, title }) => ({ ...acc, [icon]: title }),
            {}
          )
        : undefined;

    return (
      <ShadowBox mb={4}>
        <TableCell
          leftIcon={isRecurring ? 'recurring-occurence' : 'single-occurence'}
          title={fullTitle}
          leftIconColor="gold"
        />
        <TableCell
          onClick={handleEditServceType}
          title={`${title}${addonIcons ? ' +' : ''}`}
          leftIcon={icon}
          rightIcons={addonIcons}
          iconTooltips={addonIconTooltips}
          rightIconColor="gold"
        />
        <TableCell
          leftIcon="calendar"
          leftIconColor="gold"
          title={formattedDate}
          disabled={isServiceWithin24hours}
          onClick={handleEditDate}
        />
        <TableCell
          leftIcon={timeframe}
          title={timeframeTitle}
          leftIconColor="gold"
          disabled={isServiceWithin24hours}
          onClick={() => {
            setShowTimeModal(true);
          }}
        />
        <TimeWindowModal
          dismiss={() => {
            setShowTimeModal(false);
          }}
          show={showTimeModal}
          timeframe={timeframe}
          availableWindow={ALL_TIME_FRAMES}
          onSubmit={newTimeframe => {
            handleEditTimeWindow(newTimeframe);
            setShowTimeModal(false);
          }}
        />
      </ShadowBox>
    );
  };

  const isCleaning = status === 'cleaning';
  const isDisabled = isSubmitting || isCleaning;

  return (
    <React.Fragment>
      <Box flex={1} display="flex" flexDirection="column">
        <TableHeading mt={[4, 0]} px={2} mb={4}>
          Service summary
        </TableHeading>
        <TableHeading mt={[4, 0]} px={2} mb={4}>
          {fullAddress}
        </TableHeading>
        {renderCleanInfo()}
        <TableHeading px={2} mb={4}>
          Helpful hints
        </TableHeading>
        <TextArea
          height={168}
          mb={4}
          value={hintNote}
          onChange={handleChangeNote}
          placeholder="Is there anything else you'd like your housekeeper to know?"
        />
        {isNoteEditing && (
          <Button
            width={1}
            mb={4}
            type="button"
            variant="secondary"
            disabled={isDisabled}
            onClick={handleSubmitNote}
          >
            Update note
          </Button>
        )}
        {errMsg && (
          <Text pb={4} color="red" textAlign="center">
            {errMsg}
          </Text>
        )}

        {isCleaning && (
          <Text pb={4} color="red" textAlign="center">
            Cleaning is in progress at the moment
          </Text>
        )}

        <Button
          width={1}
          mb={4}
          type="button"
          variant="secondary"
          disabled={isDisabled}
          onClick={() => {
            showModal(true);
          }}
        >
          {isRecurring ? 'Skip service' : 'Cancel service'}
        </Button>
      </Box>

      {isHotelStyle && (
        <Button
          disabled={isDisabled}
          width={1}
          mb={4}
          type="button"
          onClick={handleUpgradeClean}
        >
          Upgrade to deep clean
        </Button>
      )}
      <DialogBox show={show}>
        <CancelConfirm
          cancelLabel="Keep service"
          description={
            isPaid
              ? 'You have already been charged for this service and will not be refunded. Do you still wish to cancel?'
              : 'Are you sure you would like to cancel your service?'
          }
          onSubmit={() => {
            showModal(false);
            handleCancelBooking();
          }}
          onCancel={() => {
            showModal(false);
          }}
        />
      </DialogBox>
    </React.Fragment>
  );
}

CleanEdit.propTypes = {
  currentClean: PropTypes.object,
  frequencyData: PropTypes.object,
  loadClean: PropTypes.func,
  setCleanEditing: PropTypes.func,
  isPMUser: PropTypes.bool,
  recipient: PropTypes.any,
  updateCurrentClean: PropTypes.func
};

const mapStatesToProps = state => ({
  currentClean: CleansSelectors.selectCurrentClean(state),
  frequencyData: AppSelectors.selectFrequencyData(state),
  isPMUser: AuthSelectors.selectIsPMUser(state),
  recipient: BookingSelectors.selectRecipientInfo(state)
});

const mapDispatchToProps = dispatch => ({
  loadClean: bookingId =>
    dispatch(CleansActions.setMyCleanDetailLoadRequest(bookingId)),
  setCleanEditing: (step, data) =>
    dispatch(CleansActions.setCurrentCleanEditing(step, data)),
  updateCurrentClean: (bookingId, result) =>
    dispatch(CleansActions.setMyCleanDetailUpdateRequest(bookingId, result))
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(CleanEdit);
